import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Splash from "../pages/Splash";

/**
 * Wraps public routes. The site is gated behind a splash page when the
 * `maintenance_mode` setting is enabled. Admins always bypass the gate so
 * they can preview/edit. When an admin flips maintenance OFF, the public
 * site becomes visible to everyone.
 *
 * Special overrides:
 *   • `?as=visitor` — forces the gate to behave as if the visitor were
 *     anonymous, so admins can preview what fans will see without logging out.
 *   • `?preview=CODE` — exchanges the code for a 7-day cookie via
 *     `/api/auth/preview-unlock`. Once the cookie is set the backend reports
 *     `preview_unlocked: true` and the splash is bypassed. Lets the admin
 *     share a single magic link with select testers without giving them
 *     admin rights.
 *   • Preview / localhost hostnames — the splash is skipped entirely so
 *     the Emergent preview URL always renders the real app. Real fans on
 *     the production custom domain still see the coming-soon page until
 *     the admin flips maintenance OFF. This also stops Emergent's
 *     Builders-contest thumbnail scraper (which crawls the preview URL)
 *     from capturing the splash as the entry image.
 */

// Hostnames where the maintenance splash should NEVER render, even when
// the toggle is on. Preview/dev environments are the admin's sandbox —
// gating them is more friction than protection.
const SPLASH_BYPASS_HOSTS_SUFFIX = [".preview.emergentagent.com"];
const SPLASH_BYPASS_HOSTS_EXACT = new Set([
  "localhost",
  "127.0.0.1",
  "0.0.0.0",
]);
const shouldBypassSplashByHost = () => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname || "";
  if (SPLASH_BYPASS_HOSTS_EXACT.has(host)) return true;
  return SPLASH_BYPASS_HOSTS_SUFFIX.some((s) => host.endsWith(s));
};

export default function MaintenanceGate({ children }) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [maintenance, setMaintenance] = useState(null); // null = loading
  const [previewUnlocked, setPreviewUnlocked] = useState(false);

  const params = new URLSearchParams(location.search);
  const previewAsVisitor = params.get("as") === "visitor";
  const previewCode = (params.get("preview") || "").trim();

  useEffect(() => {
    let alive = true;

    const run = async () => {
      // If we arrived via `?preview=CODE`, redeem it first. The backend
      // sets the cookie on success; we then re-fetch /settings/maintenance
      // so the response reflects the unlocked state.
      if (previewCode) {
        try {
          await api.post("/auth/preview-unlock", { code: previewCode });
        } catch (_) {
          /* wrong/expired code — fall through and show the splash */
        }
      }
      try {
        const r = await api.get("/settings/maintenance");
        if (!alive) return;
        setMaintenance(!!r.data?.enabled);
        setPreviewUnlocked(!!r.data?.preview_unlocked);
      } catch (_) {
        if (alive) {
          // If the call fails or is blocked (e.g. an edge/bot-protection
          // rule intercepting the request for headless/crawler traffic —
          // this is exactly what breaks Google's JS-rendering pass on
          // real article pages), default to NOT gated. Failing closed
          // here used to mean any blocked fetch silently served the
          // "Coming soon" splash instead of the real page to search
          // engine renderers, which is worse than the rare case this
          // was meant to protect against.
          setMaintenance(false);
          setPreviewUnlocked(false);
        }
      }
    };

    run();
    return () => {
      alive = false;
    };
    // We only want to run this on mount + when the `preview` query changes.
  }, [previewCode]);

  if (authLoading || maintenance === null) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#0A0F17]"
        data-testid="maintenance-loading"
      >
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-[#6CABDD]" />
      </div>
    );
  }

  // Treat admin as anonymous when previewing
  const treatAsAnon = previewAsVisitor || !user || user.role !== "admin";

  // Host-based bypass: never show the splash on preview/dev hosts. Real
  // fans hit the production custom domain, so their gate is unaffected.
  const bypassByHost = shouldBypassSplashByHost();

  // Gate enabled and visitor is not admin (or admin is previewing) → splash
  // UNLESS they hold a valid preview cookie OR are on a bypass host.
  if (maintenance && treatAsAnon && !previewUnlocked && !bypassByHost) {
    return <Splash />;
  }

  return children;
}
